// platform/app/src/pages/RepoListPage.tsx
import { Link } from 'react-router-dom'
import { GitBranch, ExternalLinkIcon } from 'lucide-react'
import {
  GithubLinkButton,
  PageHeader,
  SectionHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@loop3/ui'
import { REPOS } from '@/lib/distribution'

/** Mono-font client badge that links internally to the client detail page. */
function ClientBadge({ prefix }: { prefix: string }) {
  return (
    <Link
      to={`/clients/${prefix}`}
      onClick={(e) => e.stopPropagation()}
      className="font-mono text-[0.7rem] uppercase tracking-[0.14em] text-[var(--l3-violet)] hover:underline"
    >
      [{prefix}]
    </Link>
  )
}

const MAX_KEYWORDS = 5

export function RepoListPage() {
  const repos = REPOS

  return (
    <div className="mx-auto max-w-7xl pb-16">
      <PageHeader
        eyebrow="L3OS Sync"
        title="Repos"
        actions={
          <GithubLinkButton
            href="https://github.com/Loop3Studio/l3-os/blob/main/.ops/sync.yml"
            label="View registry"
          />
        }
        subtitle={
          <>
            Every repo registered in{' '}
            <a
              href="https://github.com/Loop3Studio/l3-os/blob/main/.ops/sync.yml"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-[0.8em] hover:text-[var(--l3-violet)] hover:underline"
            >
              .ops/sync.yml
              <ExternalLinkIcon className="size-3 opacity-60" />
            </a>
            , linked to its client. Each row opens the repo on GitHub; client
            badges link to the client detail page.
          </>
        }
      />

      <section>
        <SectionHeader
          icon={GitBranch}
          label="Managed repos"
          meta={`${repos.length} ${repos.length === 1 ? 'entry' : 'entries'}`}
        />

        {repos.length === 0 ? (
          <div className="border-t border-dashed border-border/60 py-6 text-center text-sm opacity-60">
            No repos found.
          </div>
        ) : (
          <div className="border-t border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Repo</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Linear team</TableHead>
                  <TableHead className="hidden md:table-cell">
                    Description
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Keywords
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {repos.map((r) => {
                  const clients = Array.isArray(r.client)
                    ? r.client
                    : [r.client]
                  const shownKeywords = r.keywords.slice(0, MAX_KEYWORDS)
                  const extraKeywords = r.keywords.length - shownKeywords.length

                  return (
                    <TableRow key={r.repo}>
                      <TableCell>
                        <a
                          href={`https://github.com/${r.repo}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-sm transition-colors hover:text-[var(--l3-violet)]"
                        >
                          {r.repo}
                        </a>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {clients.map((p) => (
                            <ClientBadge key={p} prefix={p} />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {r.linear.team}
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell max-w-[280px]">
                        <span
                          className="text-xs opacity-70 truncate block"
                          title={r.description}
                        >
                          {r.description}
                        </span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {shownKeywords.map((kw) => (
                            <span
                              key={kw}
                              className="inline-block rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-wide opacity-70"
                            >
                              {kw}
                            </span>
                          ))}
                          {extraKeywords > 0 && (
                            <span className="inline-block rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[0.6rem] opacity-50">
                              +{extraKeywords}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  )
}
